import { supabase } from "@/integrations/supabase/client";

export type DigitalCategory = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  sort_order: number;
  active: boolean;
};

export type DigitalItem = {
  id: string;
  category_id: string | null;
  slug: string;
  name: string;
  short_description: string | null;
  description: string | null;
  price_cents: number;
  price_unit: string | null;
  currency: string;
  image_url: string | null;
  badge: string | null;
  featured: boolean;
  is_best_seller: boolean;
  is_top: boolean;
  active: boolean;
  sort_order: number;
  metadata: Record<string, any>;
  rating_avg: number;
  rating_count: number;
  stock_qty: number | null;
  stock_tracked: boolean;
  download_url: string | null;
  requires_license: boolean;
  file_size_label: string | null;
  created_at: string;
  updated_at: string;
};

export type DigitalItemReview = {
  id: string;
  item_id: string;
  user_id: string;
  rating: number;
  title: string | null;
  comment: string | null;
  published: boolean;
  created_at: string;
};

export type DigitalItemLicense = {
  id: string;
  item_id: string;
  user_id: string;
  order_id: string | null;
  key_value: string;
  status: string;
  issued_at: string;
  expires_at: string | null;
};

export async function fetchItemReviews(itemId: string): Promise<DigitalItemReview[]> {
  const { data, error } = await (supabase as any)
    .from("digital_item_reviews")
    .select("*")
    .eq("item_id", itemId)
    .eq("published", true)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as DigitalItemReview[];
}

export async function fetchMyLicenses(): Promise<DigitalItemLicense[]> {
  const { data, error } = await (supabase as any)
    .from("digital_item_licenses")
    .select("*")
    .order("issued_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as DigitalItemLicense[];
}

export async function fetchDigitalCategories(includeInactive = false): Promise<DigitalCategory[]> {
  let q = supabase.from("digital_categories").select("*").order("sort_order").order("name");
  if (!includeInactive) q = q.eq("active", true);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as DigitalCategory[];
}

export async function fetchDigitalItems(includeInactive = false): Promise<DigitalItem[]> {
  let q = supabase
    .from("digital_items")
    .select("*")
    .order("featured", { ascending: false })
    .order("sort_order")
    .order("name");
  if (!includeInactive) q = q.eq("active", true);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as DigitalItem[];
}

export function formatItemPrice(item: Pick<DigitalItem, "price_cents" | "currency" | "price_unit">) {
  const price = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: item.currency || "USD",
  }).format((item.price_cents || 0) / 100);
  return item.price_unit ? `${price} / ${item.price_unit}` : price;
}

/**
 * Group items by normalized name to surface duplicates.
 */
export function findDuplicateGroups(items: DigitalItem[]) {
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  const map = new Map<string, DigitalItem[]>();
  for (const it of items) {
    const key = norm(it.name);
    if (!key) continue;
    const arr = map.get(key) ?? [];
    arr.push(it);
    map.set(key, arr);
  }
  return Array.from(map.entries())
    .filter(([, arr]) => arr.length > 1)
    .map(([key, arr]) => ({ key, items: arr }))
    .sort((a, b) => b.items.length - a.items.length);
}
